import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { colors } from '../theme';

const CombinationStarterPanels: React.FC = () => {
  const features = [
    'Motor starter and protection in one enclosure',
    'Overload protection',
    'Short circuit protection',
    'Custom configurations available',
    'Various enclosure ratings (NEMA 1, 3R, 4, 4X, 12)',
  ];

  const benefits = [
    'Space-saving design',
    'Simplified installation',
    'Reduced wiring complexity',
    'Enhanced safety',
    'Cost-effective solution',
    'Reliable motor control',
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Page Header */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '40%',
            height: '100%',
            background: 'linear-gradient(135deg, transparent 0%, rgba(255, 255, 255, 0.1) 100%)',
          },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Chip
              label="Products"
              component={Link}
              to="/products"
              clickable
              sx={{
                mb: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: '#FFFFFF',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            />
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                color: '#FFFFFF',
                fontWeight: 700,
                mb: 2,
              }}
            >
              Combination Starter Panels
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '1.1rem',
                maxWidth: 500,
              }}
            >
              Reliable motor control and protection in a single, compact package
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Accent Line */}
      <Box sx={{ height: 4, background: `linear-gradient(90deg, ${colors.secondary} 0%, ${colors.secondaryDark} 100%)` }} />

      {/* Main Content Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: '#FFFFFF',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 24,
                    left: 24,
                    right: -24,
                    bottom: -24,
                    border: `3px solid ${colors.primary}`,
                    borderRadius: 4,
                    zIndex: -1,
                  },
                }}
              >
                <Box
                  component="img"
                  src="/starter.jpg"
                  alt="Combination Starter Panels"
                  sx={{
                    width: '100%',
                    borderRadius: 4,
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography
                variant="overline"
                sx={{ color: colors.primary, fontWeight: 600, letterSpacing: 2 }}
              >
                Motor Control Solutions
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  color: colors.secondary,
                  mb: 3,
                  mt: 1,
                }}
              >
                Combination Starter Panels
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: colors.textSecondary,
                  lineHeight: 1.9,
                  mb: 3,
                }}
              >
                Enclosing a motor starter and electrical protection devices together increases efficient operation. Our combination starter panels provide reliable motor control and protection in a single, compact package.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: colors.textSecondary,
                  lineHeight: 1.9,
                  mb: 4,
                }}
              >
                We design and build custom combination starter panels for various industrial applications, ensuring proper motor protection and control functionality.
              </Typography>
              <Button
                component={Link}
                to="/contact"
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
              >
                Request a Quote
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features and Benefits Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: colors.background,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Features */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  border: 'none',
                  boxShadow: '0 8px 40px rgba(0, 0, 0, 0.08)',
                  position: 'relative',
                  overflow: 'visible',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
                    borderRadius: '12px 12px 0 0',
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      color: colors.secondary,
                      mb: 3,
                      fontWeight: 700,
                    }}
                  >
                    Features
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {features.map((feature, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <CheckCircleOutlineIcon sx={{ color: colors.primary, fontSize: 22, mt: 0.25 }} />
                        <Typography
                          variant="body1"
                          sx={{
                            color: colors.textSecondary,
                            lineHeight: 1.6,
                          }}
                        >
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Benefits */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  border: 'none',
                  boxShadow: '0 8px 40px rgba(0, 0, 0, 0.08)',
                  position: 'relative',
                  overflow: 'visible',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: `linear-gradient(90deg, ${colors.secondary} 0%, ${colors.secondaryDark} 100%)`,
                    borderRadius: '12px 12px 0 0',
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      color: colors.secondary,
                      mb: 3,
                      fontWeight: 700,
                    }}
                  >
                    Benefits
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {benefits.map((benefit, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <CheckCircleOutlineIcon sx={{ color: colors.primary, fontSize: 22, mt: 0.25 }} />
                        <Typography
                          variant="body1"
                          sx={{
                            color: colors.textSecondary,
                            lineHeight: 1.6,
                          }}
                        >
                          {benefit}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: colors.secondary,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                color: '#FFFFFF',
                mb: 3,
              }}
            >
              Need a Custom Combination Starter Panel?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: '1.1rem',
                mb: 4,
                maxWidth: 500,
                mx: 'auto',
              }}
            >
              Our team can design and build custom panels to meet your exact specifications.
            </Typography>
            <Button
              component={Link}
              to="/contact"
              variant="contained"
              size="large"
              sx={{
                backgroundColor: colors.primary,
                '&:hover': {
                  backgroundColor: colors.primaryDark,
                },
              }}
            >
              Get Started Today
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Back to Products */}
      <Box
        sx={{
          py: 4,
          background: '#FFFFFF',
          borderTop: `1px solid ${colors.border}`,
          textAlign: 'center',
        }}
      >
        <Button
          component={Link}
          to="/products"
          variant="outlined"
          color="secondary"
          startIcon={<ArrowBackIcon />}
        >
          Back to All Products
        </Button>
      </Box>
    </Box>
  );
};

export default CombinationStarterPanels;
